// Pure reads behind Documents (`/documents`): the company library (price sheets, brochures, contracts) as the iPhone
// lists it, a read-only port of GroundTeam/Views/Home/DocumentsView.swift and Home+Store.swift: grouped by category
// ("General" first, then A–Z; a blank category counts as General), names A–Z inside each, the search on name,
// category and file name, and the file-kind icon and color of `DocumentRow` from the file's extension.
//
// The size iOS prints next to each document comes from its own copy of the file (`documentSizeText`); the synced
// item carries no size, so the console leaves it out.

import { DEFAULT_DOCUMENT_CATEGORY, type DocumentPayload, type UserPayload } from '@/contracts/models';
import { parseWireDate } from '@/contracts/wireDate';
import { formatShortDate } from '@/domain/format';
import { compareLocalized, fullName, lookup, sameId, trimSpaces } from '@/domain/util';
import { contentTypeFor } from '@/sync/files';

/** A documents row as the list needs it (envelope id, best-effort value, file path, newer-app flag). */
export interface DocumentSource {
  id: string;
  value: DocumentPayload;
  /** `<company>/documents/<fileName>`; absent when the item synced without its file. */
  filePath?: string;
  undecodable?: string;
}

/** `DocumentsView` / `documentCategories`: a blank category files under General. */
export function documentCategory(d: Pick<DocumentPayload, 'category'>): string {
  return trimSpaces(d.category) === '' ? DEFAULT_DOCUMENT_CATEGORY : d.category;
}

/** `(fileName as NSString).pathExtension.lowercased()`: "pdf", "xlsx", "" when there is none. */
export function documentExtension(fileName: string): string {
  const base = fileName.slice(fileName.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  return dot > 0 && dot < base.length - 1 ? base.slice(dot + 1).toLowerCase() : '';
}

export type DocumentKind = 'pdf' | 'image' | 'text' | 'sheet' | 'slides' | 'video' | 'other';

/** `DocumentRow.icon` / `.tint` by extension. */
export function documentKind(ext: string): DocumentKind {
  switch (ext) {
    case 'pdf':
      return 'pdf';
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'heic':
    case 'gif':
      return 'image';
    case 'doc':
    case 'docx':
    case 'pages':
      return 'text';
    case 'xls':
    case 'xlsx':
    case 'csv':
    case 'numbers':
      return 'sheet';
    case 'ppt':
    case 'pptx':
    case 'key':
      return 'slides';
    case 'mp4':
    case 'mov':
      return 'video';
    default:
      return 'other';
  }
}

/** `DocumentRow.tint` (Theme red, purple, navy, green, orange, teal). */
export const DOCUMENT_KIND_COLORS: Readonly<Record<DocumentKind, string>> = {
  pdf: '#DB4038',
  image: '#7D4DBF',
  text: '#102A5E',
  sheet: '#29A85C',
  slides: '#F96A3C',
  video: '#009E9E',
  other: '#102A5E',
};

/** Spoken kind, for screen readers and the type line when a file has no extension. */
export const DOCUMENT_KIND_TITLES: Readonly<Record<DocumentKind, string>> = {
  pdf: 'PDF',
  image: 'Image',
  text: 'Document',
  sheet: 'Spreadsheet',
  slides: 'Presentation',
  video: 'Video',
  other: 'File',
};

// iOS stores an imported file as "<UUID>-<original name>.<ext>" (AppStore.storedDocumentFileName).
const STORED_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i;

/** The name to show: the document's name, else the original file name, else "Untitled document". */
export function documentDisplayName(d: Pick<DocumentPayload, 'name' | 'fileName'>): string {
  const name = trimSpaces(d.name);
  if (name !== '') return name;
  const file = trimSpaces(d.fileName.replace(STORED_PREFIX, ''));
  return file !== '' ? file : 'Untitled document';
}

export interface DocumentRowModel {
  /** Envelope id. */
  id: string;
  name: string;
  fileName: string;
  category: string;
  /** Upper-case extension for the type line ("PDF"); "" without one. */
  ext: string;
  kind: DocumentKind;
  color: string;
  /** "PDF", or the kind ("File") when the name has no extension. */
  typeLabel: string;
  addedAt?: Date;
  addedBy?: UserPayload;
  /** For `useFileUrl`; absent when the item has no file. */
  filePath?: string;
  /**
   * The browser shows it in a tab (iOS uploads PDFs, JPEGs and PNGs with their type; anything else is stored as
   * binary, so the browser downloads it).
   */
  previewable: boolean;
  /** Written by a newer app (shown with the "Newer app" badge). */
  newer: boolean;
}

export function documentRow(source: DocumentSource, users: ReadonlyMap<string, UserPayload>): DocumentRowModel {
  const d = source.value;
  const ext = documentExtension(d.fileName);
  const kind = documentKind(ext);
  const row: DocumentRowModel = {
    id: source.id,
    name: documentDisplayName(d),
    fileName: d.fileName,
    category: documentCategory(d),
    ext: ext.toUpperCase(),
    kind,
    color: DOCUMENT_KIND_COLORS[kind],
    typeLabel: ext !== '' ? ext.toUpperCase() : DOCUMENT_KIND_TITLES[kind],
    previewable: contentTypeFor(d.fileName) !== 'application/octet-stream',
    newer: source.undecodable !== undefined,
  };
  const added = parseWireDate(d.addedAt);
  if (added) row.addedAt = added;
  const by = lookup(users, d.addedBy);
  if (by) row.addedBy = by;
  if (source.filePath) row.filePath = source.filePath;
  return row;
}

/**
 * `DocumentsView.filtered`: name, category or file name contains the query (case-insensitive, trimmed). The category
 * is the one shown, so "general" also finds documents with a blank category.
 */
export function documentMatches(d: Pick<DocumentPayload, 'name' | 'category' | 'fileName'>, query: string): boolean {
  const q = trimSpaces(query).toLowerCase();
  if (q === '') return true;
  return (
    d.name.toLowerCase().includes(q) ||
    documentCategory(d).toLowerCase().includes(q) ||
    d.fileName.toLowerCase().includes(q)
  );
}

/** `documentCategories` order: General first, then A–Z (localized, case-insensitive). */
export function compareCategories(a: string, b: string): number {
  if (a === b) return 0;
  if (a === DEFAULT_DOCUMENT_CATEGORY) return -1;
  if (b === DEFAULT_DOCUMENT_CATEGORY) return 1;
  return compareLocalized(a, b);
}

export interface DocumentGroup {
  category: string;
  rows: DocumentRowModel[];
}

export interface DocumentLibrary {
  /** Categories with at least one match, in order; names A–Z inside each. */
  groups: DocumentGroup[];
  /** Every document in the library (before the search). */
  total: number;
  /** Documents matching the search. */
  matched: number;
  /** Distinct categories in the whole library. */
  categories: number;
}

/** The library as the page shows it: search, then group by category and sort (`DocumentsView.list`). */
export function documentLibrary(
  items: readonly DocumentSource[],
  options: { users: ReadonlyMap<string, UserPayload>; query?: string },
): DocumentLibrary {
  const query = options.query ?? '';
  const byCategory = new Map<string, DocumentRowModel[]>();
  const allCategories = new Set<string>();
  let matched = 0;
  for (const item of items) {
    const category = documentCategory(item.value);
    allCategories.add(category);
    if (!documentMatches(item.value, query)) continue;
    matched += 1;
    const rows = byCategory.get(category);
    const row = documentRow(item, options.users);
    if (rows) rows.push(row);
    else byCategory.set(category, [row]);
  }
  const groups = [...byCategory.keys()].sort(compareCategories).map((category) => ({
    category,
    rows: byCategory.get(category)!.sort((a, b) => compareLocalized(a.name, b.name)),
  }));
  return { groups, total: items.length, matched, categories: allCategories.size };
}

/**
 * The line under a document's name: "PDF · Added Sep 24, 2026 by Ana Ruiz" ("by you" for the viewer's own; the date
 * and the person each only when known).
 */
export function documentMetaLine(
  row: Pick<DocumentRowModel, 'typeLabel' | 'addedAt' | 'addedBy'>,
  options: { viewerId?: string | null; locale?: string } = {},
): string {
  const who = row.addedBy ? (sameId(row.addedBy.id, options.viewerId) ? 'you' : fullName(row.addedBy) || 'Unnamed') : null;
  const when = row.addedAt ? ` ${formatShortDate(row.addedAt, options.locale)}` : '';
  if (when === '' && who === null) return row.typeLabel;
  return `${row.typeLabel} · Added${when}${who ? ` by ${who}` : ''}`;
}
